/* ═══════════════════════ SYNC-KØ ═══════════════════════
   Offline-først skrivekø. Operasjoner enqueues lokalt og
   forsøkes flushed til Supabase. Hvis nett mangler: ligger
   i kø til neste 'online'-event.

   Design:
   - Optimistisk UI: lokal state oppdateres FØR enqueue
   - Persistert i localStorage så kø overlever reload
   - Permanente feil (constraint-brudd etc.) droppes med warn
   - Transiente feil (nett, timeout) holdes for retry
════════════════════════════════════════════════════════════════ */

import { getSB } from './cloud.js';

const QKEY = "ca26-sync-queue";
let q = [];
try { q = JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch {}

let flushing = false;
const listeners = new Set();

function save(){ try { localStorage.setItem(QKEY, JSON.stringify(q)); } catch {} }
function notify(){ listeners.forEach(fn => { try { fn(q.length); } catch {} }); }

export function pendingCount(){ return q.length; }
export function onChange(fn){ listeners.add(fn); return () => listeners.delete(fn); }

/**
 * Køfør en write-operasjon.
 * @param {{kind:'insert'|'upsert'|'update'|'delete', table:string, data?:any, where?:object, opts?:object}} op
 */
export function enqueue(op){
  q.push({ ...op, _id: crypto.randomUUID(), _ts: Date.now() });
  save();
  notify();
  flush(); // best-effort, returnerer raskt hvis offline
}

export async function flush(){
  if(flushing) return;
  if(!navigator.onLine) return;
  const client = getSB();
  if(!client || q.length === 0) return;
  flushing = true;
  const remaining = [];
  for(const op of q){
    try{
      let res;
      switch(op.kind){
        case 'insert': res = await client.from(op.table).insert(op.data); break;
        case 'upsert': res = await client.from(op.table).upsert(op.data, op.opts || {}); break;
        case 'update': {
          let req = client.from(op.table).update(op.data);
          for(const [k,v] of Object.entries(op.where||{})) req = req.eq(k, v);
          res = await req;
          break;
        }
        case 'delete': {
          let req = client.from(op.table).delete();
          for(const [k,v] of Object.entries(op.where||{})) req = req.eq(k, v);
          res = await req;
          break;
        }
        default: console.warn("Unknown op kind", op); continue;
      }
      if(res?.error){
        // Permanent feil (RLS, constraint, etc.) — drop med advarsel
        console.warn("Sync op failed permanently, dropping", op, res.error);
      }
    } catch(e){
      // Nettverksfeil — behold for retry
      remaining.push(op);
    }
  }
  q = remaining;
  save();
  flushing = false;
  notify();
}

/* Auto-flush når vi kommer online */
window.addEventListener("online", () => { flush(); });
/* Initial flush ved oppstart */
if(navigator.onLine) setTimeout(flush, 2000);
